<script lang="ts" setup type="text/x-template" id="editorTemplate">
import AUApi from "@/api";
import { ref, onMounted, reactive, nextTick, getCurrentInstance } from "vue";
import { getWeekdayNamesShort, getMonthNames } from "@/editor/CalenderHelper";
import {
  getKennungValue,
  getKennungTitle,
  getKennungValueDisplay,
} from "@/DataHelper";

import {
  mountedBase,
  onSave,
  BaseSetup,
  onDelete,
  createLocalSaveRequest,
} from "@/DataLoaderStatic";
import { getSiteData } from "@/NavigationHelper";
import { goBack } from "@/NavigationHelper";
import { loadLocalData, saveLocalData } from "@/BrowserDataStore";
import { valueToFormatStr } from "@/CalenderHelper";

import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
import { showSnackbar } from "@/UIHelper";
const validator = createEditorValidator();
provideEditorValidator(validator);

const emit = defineEmits([
  "back",
  "save",
  "delete",
  "setTGM",
  "backDelete",
  "queueSaveLocal",
  "localQueued",
  "deletePendingLocal",
]);
const props = defineProps<{
  aic?: number;
  siteData?: any;
  queueSaveLocal?: boolean;
  checkDateRange?: (von: Date, bis: Date, aic: number) => boolean;
  checkOneEventPerDay?: (
    von: Date,
    bis: Date,
    employeeAic: any,
    ownAicBewPool: number,
    isAbwesenheit?: boolean,
  ) => boolean;
}>();

let allColumns: DataColumn[] = [];
let saveVisible = ref<boolean>(true);
let showGanztag = ref<boolean>(false);
let showAbwesenheit = ref<boolean>(false);
let disableSwitch = ref<boolean>(false);
let value1 = ref<Date>();
let value2 = ref<Date>();
let value3 = ref<Date>();
let value4 = ref<Date>();
let memo = ref<string | undefined>(undefined);
let tag!: number;
let wochenTag = ref<string>("");
let monat = ref<string>("-");
let showVB3 = ref<boolean>(false);
let showVB4 = ref<boolean>(false);
let showAuftrag1 = ref<boolean>(false);
let showAuftrag2 = ref<boolean>(false);
let showAuftrag3 = ref<boolean>(false);
let showAuftrag4 = ref<boolean>(false);
let selected = ref<number>(0);
let deleteVisible = ref<boolean>(false);
let setKey = ref<number>(0);
let tagesmuster = ref<string>("");
const siteData = props.siteData ?? getSiteData() ?? {};
const pageParams: DataPageParameter = {
  zeitbereich: siteData.zeitbereich ?? AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_TE_TGM_Editierbar",
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  pageParams.queryAIC = props.aic ?? siteData.aic ?? pageParams.queryAIC;
  pageParams.zeitbereich = siteData.zeitbereich ?? pageParams.zeitbereich;
  if (siteData.abwesenheit) {
    showAbwesenheit.value = true;
  }
  if (siteData.selectedTGM) {
    selected.value = siteData.selectedTGM;
  }
  if (siteData.readOnlyMutable) {
    saveVisible.value = false;
    showGanztag.value = false;
  }
  if (siteData.tag) {
    pageParams.zeitbereich = {
      von: siteData.tag,
      bis: siteData.tag,
      bereich: "Tag",
    };
  } else {
    if (siteData.zeitbereich?.von) {
      const von = siteData.zeitbereich.von;
      tag = von.getDate();
      const days = getWeekdayNamesShort();
      wochenTag.value = days[von.getDay()];
      monat.value = getMonthNames()[von.getMonth()];
    }
  }
  await mountedBase(uiData, pageParams, false);
  if (!uiData.showLoading && uiData.data) {
    if (
      ((uiData.data[0].aic_Bew_pool && uiData.data[0].aic_Bew_pool > 0) ||
        siteData.pendingDienstplanSaveId) &&
      !siteData.readOnlyMutable
    ) {
      deleteVisible.value = true;
    }
    const isNewEntry =
      !(uiData.data[0].aic_Bew_pool && uiData.data[0].aic_Bew_pool > 0) &&
      !siteData.pendingDienstplanSaveId;
    if (isNewEntry && !siteData.selectedTGM) {
      const lastTagesmuster = loadLocalData<{
        aic: number;
        description?: string;
        bezeichnung?: string;
        row?: any;
        columns?: any;
      } | null>("dienstplanLastTagesmuster", null);
      if (lastTagesmuster?.aic) {
        // EditComboBox liest je nach Feld-Konfiguration nur den echten Datenwert
        // (getInitValue aus uiData.data[0]) aus - :selectedItem wird dabei ignoriert.
        // Deshalb den kompletten Wert (inkl. Bezeichnung) direkt ins uiData.data[0] schreiben.
        selected.value = lastTagesmuster.aic;
        const zaCol = uiData.columns.find((c: any) => c.kennung === "ZA");
        if (zaCol) {
          uiData.data[0][zaCol.name] = lastTagesmuster;
        }
        // EditComboBox löst beim Vorbelegen nur "initValue" aus, nicht "changeValue" -
        // setVonBis muss deshalb hier direkt aufgerufen werden, damit von/bis mit vorbelegt werden.
        if (lastTagesmuster.row) {
          setVonBis(lastTagesmuster);
        }
      }
    }
    const vb3 = getKennungTitle("vb3", uiData.columns);
    if (vb3) {
      showVB3.value = true;
    }
    const vb4 = getKennungTitle("vb4", uiData.columns);
    if (vb4) {
      showVB4.value = true;
    }
    const auftrag1 = getKennungTitle("ZE_DIENSTPLAN_AUFTRAG_1", uiData.columns);
    if (auftrag1) {
      showAuftrag1.value = true;
    }
    const auftrag2 = getKennungTitle("ZE_DIENSTPLAN_AUFTRAG_2", uiData.columns);
    if (auftrag2) {
      showAuftrag2.value = true;
    }
    const auftrag3 = getKennungTitle("ZE_DIENSTPLAN_AUFTRAG_3", uiData.columns);
    if (auftrag3) {
      showAuftrag3.value = true;
    }
    const auftrag4 = getKennungTitle("ZE_DIENSTPLAN_AUFTRAG_4", uiData.columns);
    if (auftrag4) {
      showAuftrag4.value = true;
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
async function onValidate() {
  return await validator.validateAsync();
}

// EditDateRange2 liefert bei reinen Uhrzeit-Feldern (vb1/vb3/vb4, oder vb wenn nicht ganztägig)
// nur die Uhrzeit - das Datum steht dabei auf 1.1.1970. Datum muss dann vom Zeitbereich-Tag übernommen werden.
function combineTimeWithReferenceDay(
  value: any,
  referenceDay: Date,
): Date | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  if (date.getFullYear() < 2000) {
    const combined = new Date(referenceDay);
    combined.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return combined;
  }
  return date;
}

function getLocalPreviewDateRange() {
  const fallbackStart =
    props.siteData?.zeitbereich?.von ?? pageParams.zeitbereich?.von;
  const fallbackEnd =
    props.siteData?.zeitbereich?.bis ??
    pageParams.zeitbereich?.bis ??
    fallbackStart;
  const referenceDay = new Date(fallbackStart);

  // mehrere Zeitbereiche möglich (Auftrag 1-4 Splits) - kleinstes Von & größtes Bis
  // aus den tatsächlich befüllten EditDateRange2-Feldern verwenden.
  // value1/value2 (vb/vb1) enthalten nach einem Tagesmuster-Wechsel die aktuell richtigen
  // Werte und werden deshalb vor uiData.data[0] bevorzugt.
  const ranges: { kennung: string; override?: any }[] = [
    { kennung: "vb", override: value1.value },
    { kennung: "vb1", override: value2.value },
    { kennung: "vb3" },
    { kennung: "vb4" },
  ];

  let start: Date | undefined;
  let end: Date | undefined;
  let isAllDay = false;
  for (const { kennung, override } of ranges) {
    const vonBis =
      override ?? getKennungValue(uiData.data[0], kennung, uiData.columns);
    if (!vonBis) {
      continue;
    }
    const von = combineTimeWithReferenceDay(vonBis.von, referenceDay);
    if (von && (!start || von.getTime() < start.getTime())) {
      start = von;
    }
    const bis = combineTimeWithReferenceDay(vonBis.bis, referenceDay);
    if (bis && (!end || bis.getTime() > end.getTime())) {
      end = bis;
    }
    if (vonBis.IsAllDay) {
      isAllDay = true;
    }
  }

  const fallbackStartDate = referenceDay;
  const fallbackEndDate = new Date(fallbackEnd ?? fallbackStart);
  const startTime = start ?? fallbackStartDate;
  let endTime = end ?? fallbackEndDate;

  if (endTime.getTime() <= startTime.getTime()) {
    endTime = new Date(startTime);
    endTime.setHours(endTime.getHours() + 1);
  }

  return {
    startTime,
    endTime,
    isAllDay,
  };
}

function toPreviewText(value: any): string {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (typeof value === "object") {
    const objectText =
      value.bezeichnung ??
      value.title ??
      value.text ??
      value.label ??
      value.name ??
      value.kennung ??
      value.display ??
      value.value;
    return objectText === value ? "" : toPreviewText(objectText);
  }
  return "";
}

function getPreviewValue(...kennungen: string[]) {
  for (const kennung of kennungen) {
    const displayValue = toPreviewText(
      getKennungValueDisplay(uiData.data[0], kennung, uiData.columns),
    );
    if (displayValue) {
      return displayValue;
    }
    const rawValue = toPreviewText(
      getKennungValue(uiData.data[0], kennung, uiData.columns),
    );
    if (rawValue) {
      return rawValue;
    }
  }
  return "";
}

function createLocalPreviewEvent() {
  const dateRange = getLocalPreviewDateRange();
  const groupId =
    props.aic ??
    props.siteData?.aic ??
    pageParams.queryAIC ??
    props.siteData?.groupID;
  const subject = `${valueToFormatStr(dateRange.startTime, "HH:mm")} - ${valueToFormatStr(dateRange.endTime, "HH:mm")}`;
  const location = getPreviewValue("COST_CENTRE", "location");
  const memoValue = getPreviewValue("MEMO");
  return {
    Subject: subject,
    IcsSubject: subject,
    StartTime: dateRange.startTime,
    EndTime: dateRange.endTime,
    startDate: dateRange.startTime,
    IsAllDay: dateRange.isAllDay,
    GroupID: groupId,
    idField: groupId,
    id: undefined,
    name: getPreviewValue("MA"),
    Location: location,
    LocationText: getKennungTitle("location", uiData.columns),
    Description: memoValue,
    Description2: "Lokal vorgemerkt",
    CategoryColor: "#fff8e8",
    dienstplan: true,
  };
}

async function save() {
  const isvalid = await onValidate();
  if (!isvalid.valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        isvalid.invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  if (props.checkOneEventPerDay) {
    // pro Tag ist nur ein Dienstplan-Eintrag pro Mitarbeiter erlaubt
    const dayStart = new Date(pageParams.zeitbereich.von);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    const employeeAic =
      props.aic ?? siteData.aic ?? pageParams.queryAIC ?? siteData.groupID;
    if (
      !props.checkOneEventPerDay(
        dayStart,
        dayEnd,
        employeeAic,
        uiData.data[0]?.aic_Bew_pool ?? 0,
        false,
      )
    ) {
      return;
    }
  }
  if (props.queueSaveLocal) {
    const request = createLocalSaveRequest(
      uiData,
      pageParams,
      0,
      pageParams.zeitbereich,
      pageParams.queryAIC,
    );
    const title =
      `${getPreviewValue("MA") || "Mitarbeiter"} ${wochenTag.value} ${
        tag || ""
      }`.trim();
    emit("queueSaveLocal", {
      title,
      request,
      previewEvent: createLocalPreviewEvent(),
      replaceId: siteData.pendingDienstplanSaveId,
    });
    await nextTick(() => emit("localQueued"));
    return;
  }
  const success = await onSave(
    uiData,
    pageParams,
    0,
    "",
    pageParams.zeitbereich,
    pageParams.queryAIC,
  );
  await nextTick(() => emit("save"));
}
async function bewDelete() {
  if (siteData.pendingDienstplanSaveId || siteData.abwAicAbwesenheiten) {
    // nur die lokale Änderung verwerfen - der ursprüngliche Datensatz bleibt bestehen
    emit(
      "deletePendingLocal",
      siteData.pendingDienstplanSaveId,
      siteData.abwAicAbwesenheiten,
    );
    return;
  }
  if (uiData.data[0] && uiData.data[0].aic_Bew_pool) {
    emit("delete", uiData.data[0].aic_Bew_pool);
  }
}

// value1/value2 halten den nach einem Tagesmuster-Wechsel bevorzugten Von/Bis-Wert
// (siehe getLocalPreviewDateRange). Bei manueller Änderung der Von/Bis-Felder muss
// dieser Override mit aktualisiert werden, sonst bleibt die Vorschau am alten TGM-Wert hängen.
function onVbChanged(val: any) {
  value1.value = val;
}
function onVb1Changed(val: any) {
  value2.value = val;
}

function setVonBis(selected: any) {
  if (selected) {
    uiData.initialized = false;
    if (typeof selected.aic === "number") {
      // letztes gewähltes Tagesmuster merken - Vorschlag beim nächsten NEUEN Eintrag
      // (row/columns mitspeichern, damit von/bis beim Vorbelegen erneut per setVonBis gesetzt werden können)
      saveLocalData("dienstplanLastTagesmuster", {
        aic: selected.aic,
        description: selected.description,
        bezeichnung: selected.bezeichnung,
        row: selected.row,
        columns: selected.columns,
      });
    }
    const kennung = "vb";
    const col = uiData.columns.find((c: any) => c.kennung === kennung);
    if (selected.row) {
      const val = getKennungValue(selected.row, kennung, selected.columns);
      value1.value = val;
      if (col) {
        uiData.data[0][col.name] = val;
      }
      const kennung1 = "vb1";
      const col1 = uiData.columns.find((c: any) => c.kennung === kennung1);
      const val1 = getKennungValue(selected.row, kennung1, selected.columns);
      value2.value = val1;
      if (col1) {
        uiData.data[0][col1.name] = value2;
      }
      // 3 & 4 muss ich bei Wechsel auch leer stellen da das nicht initialisiert werden kann!
      value3.value = undefined;
      value4.value = undefined;
      const memoValue = getKennungValue(uiData.data[0], "MEMO", uiData.columns);
      if (!memoValue || uiData.data[0].aic_Bew_pool === 0) {
        const valMemo = getKennungValue(
          selected.row,
          "ZE_MEMO_SCHICHTPLAN",
          selected.columns,
        );
        memo.value = valMemo;
        const kennung2 = "MEMO";
        const col2 = uiData.columns.find((c: any) => c.kennung === kennung2);
        if (col2) {
          uiData.data[col2.name] = memo;
        }
      }
    }
    emit("setTGM", selected);
    uiData.initialized = true;
    setKey.value += +1;
  }
}
</script>
<template id="editorTemplate">
  <v-sheet cols="12" xs="12" sm="6">
    <v-card
      v-if="!uiData.showLoading"
      class="dvhDialog"
      :loading="uiData.showLoading"
    >
      <v-toolbar
        density="compact"
        class="subtitle-1 dvhBackground lighten-2 pl-1"
        style="color: white"
      >
        <div style="color: white">
          {{ getKennungValueDisplay(uiData.data[0], "MA", uiData.columns) }}
        </div>
        <v-spacer class="pl-0"></v-spacer>
        <div class="headline font-weight-light white--text pl-4">
          {{ wochenTag }}
        </div>
        <strong class="display-2 font-weight-regular white--text"
          >{{ tag }}.</strong
        >

        <span class="headline font-weight-light white--text pr-5">
          {{ monat }}
        </span>

        <v-switch
          class="mr-4"
          v-model="showAbwesenheit"
          color="success"
          :disabled="disableSwitch"
          base-color="white"
          dense
          hide-details
        >
        </v-switch>
        <v-divider class="px-2" vertical></v-divider>
        <v-btn @click="goBack()" icon="mdi-close"> </v-btn>
      </v-toolbar>

      <v-card-text
        v-if="!showAbwesenheit && uiData.initialized"
        class="custom-event-editor pt-3"
      >
        <EditComboBox
          :disabled="!saveVisible"
          kennung="ZA"
          id="EventType"
          :name="getKennungTitle('Tagesmuster', uiData.columns)"
          style="width: 100%"
          @changeValue="setVonBis"
          :selectedItem="selected"
          :modelValue="tagesmuster"
          @update:modelValue="tagesmuster = $event"
        />

        <EditComboBox
          kennung="COST_CENTRE"
          :name="getKennungTitle('COST_CENTRE', uiData.columns)"
          style="width: 100%"
        />

        <v-row class="fullcol" v-if="!showAuftrag1">
          <EditDateRange2
            :disabled="!saveVisible"
            ref="dateRange"
            kennung="vb"
            :showGanztag="showGanztag"
            class="pb-10"
            :initValue="value1"
            :key="setKey"
            @changeValue="onVbChanged"
          ></EditDateRange2>
          <EditDateRange2
            ref="dateRange"
            :disabled="!saveVisible"
            kennung="vb1"
            :showGanztag="false"
            class="pb-10"
            :initValue="value2"
            :key="setKey"
            @changeValue="onVb1Changed"
          ></EditDateRange2>
          <EditDateRange2
            v-if="showVB3"
            ref="dateRange"
            :disabled="!saveVisible"
            kennung="vb3"
            :showGanztag="false"
            class="pb-10"
            :initValue="value3"
            :key="setKey"
          ></EditDateRange2>
          <EditDateRange2
            v-if="showVB4"
            ref="dateRange"
            :disabled="!saveVisible"
            kennung="vb4"
            :showGanztag="false"
            class="pb-10"
            :initValue="value4"
            :key="setKey"
          ></EditDateRange2>
        </v-row>
        <v-row class="fullcol" v-if="showAuftrag1">
          <v-col class="fullcol">
            <EditDateRange2
              :disabled="!saveVisible"
              ref="dateRange"
              kennung="vb"
              :showGanztag="showGanztag"
              class="pb-10"
              :initValue="value1"
              :key="setKey"
              @changeValue="onVbChanged"
            ></EditDateRange2>
          </v-col>
          <v-col class="fullcol">
            <EditComboBox
              kennung="ZE_DIENSTPLAN_AUFTRAG_1"
              :name="getKennungTitle('ZE_DIENSTPLAN_AUFTRAG_1', uiData.columns)"
              style="width: 100%"
            />
          </v-col>
        </v-row>
        <v-row class="fullcol" v-if="showAuftrag2">
          <v-col class="fullcol">
            <EditDateRange2
              :disabled="!saveVisible"
              ref="dateRange"
              kennung="vb1"
              :showGanztag="showGanztag"
              class="pb-10"
              :initValue="value2"
              :key="setKey"
              @changeValue="onVb1Changed"
            ></EditDateRange2>
          </v-col>
          <v-col class="fullcol">
            <EditComboBox
              kennung="ZE_DIENSTPLAN_AUFTRAG_2"
              :name="getKennungTitle('ZE_DIENSTPLAN_AUFTRAG_2', uiData.columns)"
              style="width: 100%"
            />
          </v-col>
        </v-row>
        <v-row class="fullcol" v-if="showVB3 && showAuftrag3">
          <v-col class="fullcol">
            <EditDateRange2
              v-if="showVB3"
              ref="dateRange"
              :disabled="!saveVisible"
              kennung="vb3"
              :showGanztag="false"
              class="pb-10"
              :initValue="value3"
              :key="setKey"
            ></EditDateRange2>
          </v-col>
          <v-col class="fullcol">
            <EditComboBox
              kennung="ZE_DIENSTPLAN_AUFTRAG_3"
              :name="getKennungTitle('ZE_DIENSTPLAN_AUFTRAG_3', uiData.columns)"
              style="width: 100%"
            />
          </v-col>
        </v-row>
        <v-row class="fullcol" v-if="showVB4 && showAuftrag4">
          <v-col class="fullcol">
            <EditDateRange2
              v-if="showVB4"
              ref="dateRange"
              :disabled="!saveVisible"
              kennung="vb4"
              :showGanztag="false"
              class="pb-10"
              :initValue="value4"
              :key="setKey"
            ></EditDateRange2>
          </v-col>
          <v-col class="fullcol">
            <EditComboBox
              kennung="ZE_DIENSTPLAN_AUFTRAG_4"
              :name="getKennungTitle('ZE_DIENSTPLAN_AUFTRAG_4', uiData.columns)"
              style="width: 100%"
            />
          </v-col>
        </v-row>

        <EditTextArea
          id="Description"
          :disabled="!saveVisible"
          kennung="MEMO"
          name="Bemerkung"
          :initValue="memo"
        />
      </v-card-text>
      <v-card-actions
        v-if="!showAbwesenheit && uiData.initialized"
        style="padding-right: 10px"
      >
        <v-spacer></v-spacer>
        <CommonBaseButtons
          :createVisible="false"
          :refreshVisible="false"
          :deleteVisible="deleteVisible"
          :saveVisible="saveVisible"
          @back="goBack()"
          @delete="bewDelete"
          @save="save"
        ></CommonBaseButtons>
      </v-card-actions>

      <v-card-text v-if="showAbwesenheit" style="padding: 0px">
        <DialogDienstplan
          :aic="pageParams.queryAIC"
          :zeitbereichUebergabe="siteData.zeitbereich"
          :siteData="siteData"
          :checkOneEventPerDay="props.checkOneEventPerDay"
          @save="$emit('save')"
          @queueSaveLocal="$emit('queueSaveLocal', $event)"
          @localQueued="$emit('localQueued')"
          @backDelete="$emit('backDelete')"
          @deletePendingLocal="
            (id, abw) => $emit('deletePendingLocal', id, abw)
          "
          :queueSaveLocal="props.queueSaveLocal"
          :backOnSave="true"
        ></DialogDienstplan>
      </v-card-text>
    </v-card>
  </v-sheet>
</template>

<style scoped>
.fullcol {
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 0px;
  margin: 0px;
}
.dvhDialog {
  padding: 0px;
}
</style>
